import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import type { ColDef, ICellRendererParams } from 'ag-grid-community';

import AGTable from 'components/ag-table/AGTable';
import AampeTooltip from 'components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';
import { FormulaType } from '../../../../../constants/formulaType';
import ICONS from '../../../../../constants/icons';
import routes from '../../../../../constants/routes';

import useGetLocationState from 'hooks/helper/useGetLocationState';

import { ALL_USERS_AUDIENCE_ID } from 'models/audience.model';
import {
    ApprovedFormulaState,
    FormulaStatus,
    FormulaTypesData,
    FormulaViewTabs,
} from 'models/formula.model';
import { FormulaCoverageMap } from 'models/personalization.model';

export type CoverageDrawerTableProps = {
    data: FormulaCoverageMap[];
    isLoading: boolean;
    tableName: string;
    hasWriteAccess: boolean;
};

const CoverageDrawerTable: React.FC<CoverageDrawerTableProps> = ({
    data,
    isLoading,
    tableName,
    hasWriteAccess,
}) => {
    const history = useHistory();
    const { getLocationState } = useGetLocationState(`Back to Coverage page`);

    const MessageLabel = (
        <MessageLabelBadge>
            <span>ML</span>
            <AampeTooltip title="Message group label" placement="top">
                <span>
                    <ICONS.info size={12} />
                </span>
            </AampeTooltip>
        </MessageLabelBadge>
    );

    const columns = useMemo<ColDef<FormulaCoverageMap>[]>(() => {
        return [
            {
                headerName: 'Message group',
                field: 'formula_name',
                flex: 2,
                minWidth: 190,
                resizable: true,
                sortable: true,
                autoHeight: true,
                wrapText: true,
                cellStyle: {
                    borderRight: `1px solid ${aampeTheme.colors.interactiveWG01}`,
                },
                cellRenderer: (
                    params: ICellRendererParams<FormulaCoverageMap>,
                ) => (
                    <MessageColumn>
                        <MessageName
                            href={
                                params.data?.surface_id
                                    ? routes.surfaceFormulaDetails(
                                          params?.data?.surface_id,
                                          params?.data?.formula_id,
                                      )
                                    : routes.formulaView(
                                          params?.data?.formula_state,
                                          params?.data?.formula_id,
                                      )
                            }
                            target="_blank"
                            rel="noreferrer"
                        >
                            {params?.value ?? 'Unnamed'}
                        </MessageName>
                        <ChannelStatusSection>
                            <ChannelSection>
                                {
                                    FormulaTypesData[
                                        params?.data
                                            ?.message_type as FormulaType
                                    ].title
                                }
                            </ChannelSection>
                            <ChannelSection>-</ChannelSection>
                            <ChannelSection>
                                {params?.data?.formula_state}
                            </ChannelSection>
                        </ChannelStatusSection>
                    </MessageColumn>
                ),
            },
            {
                headerName: 'Labels',
                field: 'labels',
                flex: 1.15,
                minWidth: 155,
                autoHeight: true,
                tooltipValueGetter: () => null,
                cellStyle: {
                    borderRight: `1px solid ${aampeTheme.colors.interactiveWG01}`,
                },
                sortable: true,
                cellRenderer: (
                    params: ICellRendererParams<FormulaCoverageMap>,
                ) => {
                    const isReviewOrLive =
                        params?.data?.formula_state &&
                        [
                            FormulaStatus.Review,
                            ApprovedFormulaState.Live,
                        ].includes(params?.data?.formula_state);

                    const query = isReviewOrLive
                        ? `tab=${FormulaViewTabs.ALTERNATES}`
                        : `current_step=alternates`;

                    const href = `${routes.formulaView(
                        params?.data?.formula_state,
                        params?.data?.formula_id,
                    )}?${query}`;

                    return (
                        <LabelContainer>
                            {params.value.map((item: any, index: number) => (
                                <LabelItem key={index}>
                                    <LabelName>{item.label_name}</LabelName>
                                    <LabelBadge
                                        href={
                                            item.label_count === 0 &&
                                            hasWriteAccess
                                                ? href
                                                : undefined
                                        }
                                        count={item.label_count}
                                        isFormulaLabel={
                                            item.label_count == 1 &&
                                            item.is_formula_label
                                        }
                                        hasWriteAccess={hasWriteAccess}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {item.label_count == 0
                                            ? 'Add'
                                            : item.label_count == 1 &&
                                              item.is_formula_label
                                            ? MessageLabel
                                            : item.label_count}
                                    </LabelBadge>
                                </LabelItem>
                            ))}
                        </LabelContainer>
                    );
                },
            },
            {
                headerName: 'Audience',
                field: 'audience_name',
                flex: 1,
                minWidth: 155,
                sortable: true,
                cellRenderer: (
                    params: ICellRendererParams<FormulaCoverageMap>,
                ) => (
                    <>
                        {params.value ? (
                            <AudienceLink
                                href={routes.audienceDetail(
                                    params?.data?.audience_id ??
                                        ALL_USERS_AUDIENCE_ID,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {params.value}
                            </AudienceLink>
                        ) : (
                            <AllUsersSection>All Users</AllUsersSection>
                        )}
                    </>
                ),
            },
        ];
    }, [getLocationState, history]);

    return (
        <AGTable
            rowData={data}
            parentContainerStyle={{
                padding: '0px',
            }}
            pagination
            defaultPageSize={5}
            columns={columns}
            isDataLoading={isLoading}
            tableWrapperStyle={{ paddingBottom: 0 }}
            tableName={tableName}
            paginationPageSizeSelector={false}
            customHeaderRowStyle={{
                background: aampeTheme.colors.backgroundWG01,
                borderRadius: `${aampeTheme.borderRadius.default} ${aampeTheme.borderRadius.default} 0 0`,
            }}
        />
    );
};

export default CoverageDrawerTable;

const MessageColumn = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const MessageName = styled.a({
    textDecoration: 'none',
    color: aampeTheme.colors.solidA02,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    lineHeight: 1.4,
    display: '-webkit-box',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const ChannelStatusSection = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(1),
    justifyContent: 'start',
    alignItems: 'center',
});

const ChannelSection = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: aampeTheme.weights.light,
});

const LabelContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(1),
});

const LabelItem = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    justifyContent: 'space-between',
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
});

const LabelName = styled.span({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
});

const LabelBadge = styled.a.withConfig({
    shouldForwardProp: (prop) =>
        !['count', 'isFormulaLabel', 'hasWriteAccess'].includes(prop),
})<{ count?: number; isFormulaLabel?: boolean; hasWriteAccess?: boolean }>(
    ({ count, isFormulaLabel, hasWriteAccess }) => ({
        textDecoration: 'none',
        display: 'flex',
        justifyContent: count == 0 ? 'center' : 'end',
        alignItems: 'center',
        cursor: hasWriteAccess && count == 0 ? 'pointer' : undefined,
        backgroundColor:
            count == 0
                ? aampeTheme.colors.solidA01
                : count && count < 3 && !isFormulaLabel
                ? aampeTheme.colors.orange02
                : aampeTheme.colors.backgroundWG02,
        color:
            count == 0
                ? aampeTheme.colors.white
                : count && count < 3 && !isFormulaLabel
                ? aampeTheme.colors.orange06
                : aampeTheme.colors.neutral09,
        borderRadius: aampeTheme.borderRadius.default,
        fontSize: aampeTheme.size.font.textMedium,
        padding: aampeTheme.spacing4(1),
        fontWeight: aampeTheme.weights.light,
        height: '21px',
        width: '44px',
    }),
);

const MessageLabelBadge = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: aampeTheme.spacing4(1),
});

const AudienceLink = styled.a({
    textDecoration: 'none',
    color: aampeTheme.colors.solidA02,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    cursor: 'pointer',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.4,
    display: '-webkit-box',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const AllUsersSection = styled.span({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
});
